<script setup lang="ts">
import { useSidebarPrivileges } from '@entities/viewer'
import { AppRoute } from '@shared/config'
import { AppText, AppTextLink } from '@shared/ui/typography'

import AssessmentEditorToolbar from './assessment-templates/AssessmentEditorToolbar.vue'
import ModalAssessmentPreview from './assessment-templates/modals/ModalAssessmentPreview.vue'
import { useAssessmentEditorPage } from '../model/assessments/assessment-editor.hook'
import { useAssessmentPreviewModal } from '../model/assessments/assessment-preview-modal.hook'
import IntroEditor from './assessments/editor/IntroEditor.vue'
import ScreenList from './assessments/editor/ScreenList.vue'
import ScreenEditor from './assessments/editor/ScreenEditor.vue'

defineOptions({
  name: 'AssessmentDetailsPage',
})

const {
  isDirty,
  isSaving,
  isNotFound,
  hasDocument,
  blockers,
  groups,
  summary,
  hasProblems,
  hasValidated,
  attemptsCount,
  status,
  isPublished,
  load,
  save,
  publish,
  duplicate,
  validate,
  focusIssue,
  confirmLeave,
  leave,
} = useAssessmentEditorPage()

const { openPreview } = useAssessmentPreviewModal()
const { canManageAssessmentTemplates } = useSidebarPrivileges()

onMounted(() => {
  void load()
})

// The guard only GUARDS — it never cleans up. A cancelled navigation must leave the editor exactly
// as it was, and this hook does not fire on a same-route id change (that is the page hook's watch).
onBeforeRouteLeave(() => confirmLeave())

// The single cleanup path. `onUnmounted` fires on every real route leave, including the ones a
// guard let through, so nothing else needs to call `leave()`.
onUnmounted(() => {
  leave()
})
</script>

<template>
  <div class="assessment-editor">
    <div
      v-if="isNotFound"
      class="assessment-editor__empty"
    >
      <AppText name="body1-medium">
        Assessment template not found
      </AppText>
      <AppText
        name="body2"
        color="slate-500"
      >
        The address does not point at an assessment template that exists.
      </AppText>
      <AppTextLink
        name="body2"
        :href="AppRoute.Assessments"
      >
        Back to assessment templates
      </AppTextLink>
    </div>

    <template v-else-if="hasDocument">
      <AssessmentEditorToolbar
        :is-dirty="isDirty"
        :is-saving="isSaving"
        :has-validated="hasValidated"
        :summary="summary"
        :has-problems="hasProblems"
        :blockers="blockers"
        :groups="groups"
        :attempts-count="attemptsCount"
        :status="status"
        :is-published="isPublished"
        :can-manage="canManageAssessmentTemplates"
        @click:save="save"
        @click:validate="validate"
        @click:preview="openPreview"
        @click:publish="publish"
        @click:duplicate="duplicate"
        @click:issue="focusIssue"
      />

      <!-- Document-scoped, like the rubric catalog below: the intro belongs to the assessment,
           not to whichever screen happens to be selected. -->
      <IntroEditor />

      <!-- Two columns, deliberately plain: RND-34 decision A-10 ships this section
           functional and unstyled; the visual pass is a later session. The preview used to be
           a third column and is now a modal, opened from the toolbar. -->
      <div class="assessment-editor__columns">
        <ScreenList class="assessment-editor__rail" />
        <ScreenEditor class="assessment-editor__form" />
      </div>

      <!-- No document-level rubric catalog: a rubric belongs to exactly one question and is
           authored inside it (`ActivityRubric`). `data.rubrics` is still the map it is stored
           in — that is storage, not a second place to edit from. -->

      <!-- The read-only preview. It reads the editor store's getters and calls no action:
           stepping through screens here never moves the rail's selection. -->
      <ModalAssessmentPreview />
    </template>
  </div>
</template>

<style scoped lang="scss" src="./AssessmentDetailsPage.scss" />
